import React, {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { PlusCircle, Save, Brain, RotateCw, Plus } from "lucide-react";
import { Oval } from "react-loader-spinner";
import QuestionGenerator from "./QuestionGenerator";
import BackgroundLoadingBanner from "./QuestionnaireBgLoading";
import {
  AnswerTypes,
  DifficultyLevels,
  ImportanceLevels,
  QuestionTypes,
} from "./constants";
import {
  generateQuestionsWithAI,
  jobQuestionnaire,
  saveQuestion,
  questionnaireStatus,
  getJobDetails,
  addProfileToQuestion,
  removeProfileFromQuestion,
  questionnaireApprovalStatus,
  assessmentProfileApprovalStatus,
  isUploadProfilePictureQuestion,
} from "./utils";
import { Link } from "react-router-dom";

import { ConditionTypes } from "./constants";
import DraggableQuestionsList from "./DraggableQuestionList";
import { useNotification } from "./CustomNotificationSystem";
import QuestionCard from "./QuestionCard";
import AssessmentProfileService from "./services/AssessmentProfileService";
import CustomProfileSelect from "./components/assessmentProfile/CustomProfileSelect";

// ConditionValueInput Component

const QuestionnaireBuilder = forwardRef(
  (
    {
      selectedJob,
      tempJobId,
      formData,
      countQuestionsAdded,
      forAssessmentProfile = null,
    },
    ref,
  ) => {
    //const [allQuestions, setAllQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [expandedQuestions, setExpandedQuestions] = useState([]);
    const [displayLoader, setDisplayLoader] = useState(false);
    const [deletedQuestions, setDeletedQuestions] = useState([]);
    const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(null);
    const [assessmentProfile, setAssessmentProfile] = useState(
      formData?.assessmentProfile ?? null,
    );
    const [prevProfileQuestion, setPrevProfileQuestion] = useState([]);
    const notify = useNotification();
    const [isNewJob, setIsNewJob] = useState(false);
    const [isTempJob] = useState(() => {
      return !!tempJobId;
    });
    const [profiles, setProfiles] = useState({});
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [refreshProfile, setRefreshProfile] = useState(false);
    const [isQuestionnaireApproved, setIsQuestionnaireApproved] =
      useState(true); // default is set to true;
    const [isAssessmentProfileApproved, setIsAssessmentProfileApproved] =
      useState(true); // default is set to true;
    const [defaultProfile, setDefaultProfile] = useState(null);

    if (!selectedJob) {
      selectedJob = tempJobId; //either selectedJob is set or tempJobId
    }
    const mainJob = selectedJob || tempJobId;

    useImperativeHandle(
      ref,
      () =>
        ref
          ? {
            handleSave: () => {
              handleSave();
            },
            getAssessmentProfile: () => {
              return assessmentProfile;
            },
          }
          : null,
      [filteredQuestions, assessmentProfile],
    );

    const handleToggleExpand = (questionId) => {
      setExpandedQuestions((prev) =>
        prev.includes(questionId)
          ? prev.filter((id) => id !== questionId)
          : [...prev, questionId],
      );
    };

    const getProfiles = async () => {
      const profiles = await AssessmentProfileService.getAll();
      // Create a combined array by concatenating all arrays from the object
      let combinedArray = [];
      // Loop through all keys in the object
      Object.keys(profiles.profile).forEach((key) => {
        if (Array.isArray(profiles.profile[key])) {
          // Optional: Add category information to each item
          const itemsWithCategory = profiles.profile[key].map((item) => ({
            ...item,
            category: key, // Adds the original key as a "category" property
          }));

          // Add these items to our combined array
          combinedArray = combinedArray.concat(itemsWithCategory);
        }
      });

      if (assessmentProfile) {
        const selectedProfile = combinedArray.find(
          (profile) => profile._id === assessmentProfile,
        );
        const profileQuestions = selectedProfile
          ? selectedProfile.questions
          : [];
        setPrevProfileQuestion(profileQuestions);
      }
      setProfiles(combinedArray);
      setLoadingProfile(false);
      setRefreshProfile(false);
      setDefaultProfile(profiles.defaultProfile);
    };

    const handleAddFollowUp = (parentId) => {
      const newFollowUp = {
        id: Date.now().toString(),
        condition: ConditionTypes.YES_NO,
        condition_value: "yes",
        question: {
          _id: (Date.now() + 1).toString(),
          jobId: mainJob,
          question_text: "New follow-up question",
          answer_type: AnswerTypes.OPEN_ENDED,
          difficulty_level: DifficultyLevels.MEDIUM,
          importance: ImportanceLevels.MEDIUM,
          evaluation_criteria: [],
          tags: [],
          follow_up_questions: [],
          question_type: "Assessment",
        },
      };

      setFilteredQuestions((prevQuestions) => {
        const updateRecursive = (questions) => {
          return questions.map((q) => {
            if (q._id === parentId) {
              return {
                ...q,
                follow_up_questions: [
                  ...(q.follow_up_questions || []),
                  newFollowUp,
                ],
              };
            }
            if (q.follow_up_questions && q.follow_up_questions.length > 0) {
              return {
                ...q,
                follow_up_questions: q.follow_up_questions.map((fu) => ({
                  ...fu,
                  question:
                    fu.question._id === parentId
                      ? {
                        ...fu.question,
                        follow_up_questions: [
                          ...(fu.question.follow_up_questions || []),
                          newFollowUp,
                        ],
                      }
                      : fu.question.follow_up_questions
                        ? {
                          ...fu.question,
                          follow_up_questions: updateRecursive([
                            fu.question,
                          ])[0].follow_up_questions,
                        }
                        : fu.question,
                })),
              };
            }
            return q;
          });
        };

        const updatedQuestions = updateRecursive(prevQuestions);
        //setAllQuestions((prev) => updateRecursive(prev));
        return updatedQuestions;
      });

      // Ensure the question is expanded after adding a follow-up
      setExpandedQuestions((prev) => {
        if (!prev.includes(parentId)) {
          return [...prev, parentId];
        }
        return prev;
      });
    };

    const handleUpdateFollowUp = (parentId, followUpId, field, value) => {
      setFilteredQuestions((prevQuestions) => {
        const updateRecursive = (questions) => {
          return questions.map((q) => {
            // Check if this is the parent question
            if (q._id === parentId) {
              return {
                ...q,
                follow_up_questions: q.follow_up_questions.map((fu) => {
                  if (fu.id === followUpId) {
                    if (field === "condition") {
                      let newConditionValue;
                      const condition_value = ConditionTypes[value];
                      switch (condition_value) {
                        case ConditionTypes.YES_NO:
                          newConditionValue = "yes";
                          break;
                        case ConditionTypes.KEYWORD:
                          newConditionValue = {
                            matchType: "matched",
                            keywords: "",
                          };
                          break;
                        case ConditionTypes.RATING:
                          newConditionValue = { type: "lessThan", value: 5 };
                          break;
                        case ConditionTypes.CONFIDENCE:
                        case ConditionTypes.COMPLETENESS:
                          newConditionValue = 50;
                          break;
                        case ConditionTypes.LENGTH:
                          newConditionValue = { type: "lessThan", value: 0 };
                          break;
                        default:
                          newConditionValue = "";
                      }
                      return {
                        ...fu,
                        condition: value,
                        condition_value: newConditionValue,
                      };
                    }
                    if (field === "question") {
                      return { ...fu, question: { ...fu.question, ...value } };
                    }
                    return { ...fu, [field]: value };
                  }
                  return fu;
                }),
              };
            }
            // Check follow-up questions recursively
            if (q.follow_up_questions && q.follow_up_questions.length > 0) {
              return {
                ...q,
                follow_up_questions: q.follow_up_questions.map((fu) => ({
                  ...fu,
                  question: updateRecursive([fu.question])[0],
                })),
              };
            }
            return q;
          });
        };

        const updatedQuestions = updateRecursive(prevQuestions);
        //setAllQuestions((prev) => updateRecursive(prev));
        //console.log(updatedQuestions);
        return updatedQuestions;
      });
    };

    useEffect(() => {
      if (countQuestionsAdded) countQuestionsAdded(filteredQuestions);
    }, [filteredQuestions]);

    useEffect(() => {
      async function fetchJob(mainJob) {
        return await getJobDetails(mainJob, isTempJob);
      }

      if (!isTempJob) {
        // if the job is temp than it's not saved yet
        fetchJob(mainJob).then((job) => {
          setAssessmentProfile(job.assessmentProfile ?? null); // used to set the assessment profile for quesionnaire builder
          setIsQuestionnaireApproved(job.questionnaireApproval ?? true);
          setIsAssessmentProfileApproved(job.assessmentProfileApproved ?? true);
          if (
            job.questionnaireStatus &&
            job.questionnaireStatus === "generating"
          ) {
            setIsNewJob(true);
          }
        });
      }
    }, [mainJob, isTempJob]);

    useEffect(() => {
      setIsBackgroundLoading(isNewJob);
    }, [isNewJob]);
    // Check questionnaire status periodically if it's a new job
    //
    useEffect(() => {
      let intervalId;

      const checkQuestionnaireStatus = async () => {
        try {
          const data = await questionnaireStatus(mainJob);
          if (data.status === "completed") {
            setIsNewJob(false);
            const questions = await jobQuestionnaire(mainJob);

            //setAllQuestions(questions); // Replace instead of append
            setFilteredQuestions(questions.filter((q) => q.jobId === mainJob));
            clearInterval(intervalId);
          } else if (data.status === "failed") {
            setIsNewJob(false);
            notify(
              "Failed to generate base questionnaire. You can still add questions manually.",
              "error",
            );
            clearInterval(intervalId);
          } else if (data.status === "generating") {
            setLoadingProgress("");
            //setAllQuestions([]);
          }
        } catch (error) {
          console.error("Error checking questionnaire status:", error);
        }
      };

      if (isNewJob) {
        intervalId = setInterval(checkQuestionnaireStatus, 3000);
        checkQuestionnaireStatus();
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [mainJob, isNewJob, notify]);

    useEffect(() => {
      if (refreshProfile) getProfiles();
    }, [refreshProfile]);

    // For initial load on page mount
    useEffect(() => {
      getProfiles();
    }, []);

    const handleQuestionsGenerated = useCallback(async () => {
      const db_questions = await jobQuestionnaire(mainJob, isTempJob);
      setFilteredQuestions(db_questions);
      //setAllQuestions([...db_questions, ...generatedQuestions]); // Replace instead of mutating
    }, [mainJob, isTempJob]);

    const handleSave = async () => {
      const currentAssessmentProfile = forAssessmentProfile
        ? forAssessmentProfile
        : assessmentProfile;
      const questionsJSON = {
        jobId: mainJob,
        questions: JSON.parse(JSON.stringify(filteredQuestions)),
        deletedQuestions: deletedQuestions, // Include deleted questions array
        assessmentProfile: currentAssessmentProfile,
      };

      const questions = await saveQuestion(questionsJSON);
      if (questions.questions) {
        setFilteredQuestions(questions.questions);
      }
      //clear the deleted questions array after successful save
      setDeletedQuestions([]);
      if (!formData) {
        // not called from JobPost Form
        notify("Saved!", "success");
      }
    };

    const handleDeleteQuestion = (questionId) => {
      if (
        window.confirm(
          "Are you sure you want to delete this question? This action cannot be undone.",
        )
      ) {
        setFilteredQuestions((prevQuestions) => {
          const deleteRecursive = (questions) => {
            // First, check if the current question is the one to delete
            const filteredQuestions = questions.filter(
              (q) => q._id !== questionId,
            );

            // Then check follow-up questions recursively
            return filteredQuestions.map((q) => {
              if (q.follow_up_questions && q.follow_up_questions.length > 0) {
                // First, check if any direct follow-up questions need to be deleted
                const filteredFollowUps = q.follow_up_questions.filter(
                  (fu) => fu.question._id !== questionId,
                );

                // Then recursively check the remaining follow-ups
                return {
                  ...q,
                  follow_up_questions: filteredFollowUps.map((fu) => ({
                    ...fu,
                    question: deleteRecursive([fu.question])[0],
                  })),
                };
              }
              return q;
            });
          };

          const updatedQuestions = deleteRecursive(prevQuestions);

          // Update allQuestions state as well
          //setAllQuestions((prev) => deleteRecursive(prev));

          // Add to deletedQuestions array
          setDeletedQuestions((prev) => [...prev, { _id: questionId }]);

          // Remove from expandedQuestions if it's expanded
          setExpandedQuestions((prev) =>
            prev.filter((id) => id !== questionId),
          );

          return updatedQuestions;
        });
      }
    };

    const handleAddQuestionToProfile = async (questionId) => {
      if (!assessmentProfile) {
        notify("Please select assessment profile to add to", "error");
        return false;
      }

      const response = await addProfileToQuestion(
        questionId,
        assessmentProfile,
      );

      if (response.error) {
        notify(response.message, "info");
        return false;
      }

      notify("Question is added to the profile", "success");

      const updatedQuestion = response.update;
      setFilteredQuestions((prev) => {
        return prev.map((q) => {
          if (q._id === updatedQuestion._id) {
            q.attachedProfile = updatedQuestion.attachedProfile;
          }

          return q;
        });
      });
    };

    const handleRemoveQuestionFromProfile = async (questionId) => {
      if (!questionId) {
        notify("Please select question to remove", "error");
        return false;
      }

      const response = await removeProfileFromQuestion(questionId);

      if (response.error) {
        notify(response.message, "info");
        return false;
      }

      notify("Question is removed from the profile");
      const updatedQuestion = response.update;
      setFilteredQuestions((prev) => {
        return prev.map((q) => {
          if (q._id === updatedQuestion._id) {
            q.attachedProfile = updatedQuestion.attachedProfile;
          }
          return q;
        });
      });
    };

    const addNewQuestion = (data) => {
      const newQuestion = {
        _id: Date.now().toString(),
        jobId: mainJob,
        question_text: data?.question_text ?? "New question",
        answer_type: data?.answer_type ?? AnswerTypes.OPEN_ENDED,
        difficulty_level: data?.difficulty_level ?? DifficultyLevels.MEDIUM,
        importance: data?.importance ?? ImportanceLevels.MEDIUM,
        evaluation_criteria: data?.evaluation_criteria ?? [],
        follow_up_questions: data?.follow_up_questions ?? [],
        tags: data?.tags ?? [],
        category: data?.category ?? "General",
        answer_scoring: data?.answer_scoring ?? { Yes: 1, No: 0 },
        question_type: data?.question_type ?? "Assessment",
        order: filteredQuestions.length + 1,
        forAssessmentProfile,
      };
      //setAllQuestions((prev) => [...prev, newQuestion]);
      setFilteredQuestions((prev) => [...prev, newQuestion]);
    };

    const generateQuestions = async () => {
      if (!assessmentProfile) {
        notify(
          "Please select an assessment profile to generate questionnaire",
          "info",
        );
        return false;
      }

      if (!window.confirm("Are you sure?")) {
        return false;
      }
      setDisplayLoader(true);

      // Extract all existing question types
      const existingQuestionTypes = [
        ...new Set(
          filteredQuestions.map((q) => q.question_type).filter((type) => type), // Remove undefined/null
        ),
      ];

      const questionnaire = await generateQuestionsWithAI(
        mainJob,
        formData,
        assessmentProfile,
        tempJobId,
        existingQuestionTypes,
      );

      if (questionnaire.error) {
        notify(questionnaire.error, "error");

        setDisplayLoader(false);
        return false;
      }

      // Merge AI-generated questions with existing manually added questions
      const existingQuestionTexts = new Set(
        filteredQuestions.map((q) => q.question_text.toLowerCase().trim()),
      );

      const newQuestions = questionnaire.filter(
        (q) => !existingQuestionTexts.has(q.question_text.toLowerCase().trim()),
      );

      // Add new questions to existing ones
      const mergedQuestions = [...filteredQuestions, ...newQuestions];

      setFilteredQuestions(mergedQuestions);
      setDisplayLoader(false);

      if (newQuestions.length > 0) {
        notify(
          `Added ${newQuestions.length} new AI-generated questions`,
          "success",
        );
      } else {
        notify(
          "No new questions were generated (all questions already exist)",
          "info",
        );
      }
    };

    const handleProfileSelection = (profileId) => {
      // Find the matching profile and get its questions directly
      const selectedProfile = profiles.find(
        (profile) => profile._id === profileId,
      );

      const profileQuestions = selectedProfile ? selectedProfile.questions : [];

      // Get current question texts to avoid duplicates
      const currentQuestionTexts = new Set(
        filteredQuestions.map((item) =>
          item.question_text.toLowerCase().trim(),
        ),
      );

      // Filter out questions that already exist in the current list
      const newQuestions = profileQuestions.filter(
        (item) =>
          !currentQuestionTexts.has(item.question_text.toLowerCase().trim()),
      );

      // Add only the new questions that don't already exist
      newQuestions.forEach((question) => {
        addNewQuestion(question);
      });

      setAssessmentProfile(profileId);
      setPrevProfileQuestion(profileQuestions);

      // Update formData with the selected profile ID
      if (formData) {
        formData.assessmentProfile = profileId;
      }

      // Provide feedback to user
      if (newQuestions.length > 0) {
        notify(
          `Added ${newQuestions.length} questions from the selected profile`,
          "success",
        );
      } else {
        notify("No new questions to add from this profile", "info");
      }
    };

    const handleRefreshProfileList = () => {
      setRefreshProfile(true);
    };

    const handleQuestionnaireApproval = async () => {
      if (
        !window.confirm(
          "Are you sure? This will send an assessment invite to all jobseekers who applied for this position.",
        )
      ) {
        return false;
      }
      const job = await questionnaireApprovalStatus(mainJob, true);
      const approval = job.questionnaireApproval;
      setIsQuestionnaireApproved(approval);
      if (approval) notify("Questionnaire approved");
      else {
        notify("Questionnaire failed to approved, please try again");
      }
    };

    const handleAssessmentProfileApproval = async () => {
      if (
        !window.confirm(
          "Are you sure? This will approve the assessment profile for this job.",
        )
      ) {
        return false;
      }
      const job = await assessmentProfileApprovalStatus(mainJob, true);
      const approval = job.assessmentProfileApproved;
      setIsAssessmentProfileApproved(approval);
      if (approval) notify("Assessment profile approved");
      else {
        notify("Assessment profile failed to approve, please try again");
      }
    };

    useEffect(() => {
      if (defaultProfile && defaultProfile._id)
        if (defaultProfile && defaultProfile._id && !assessmentProfile) {
          handleProfileSelection(defaultProfile._id);
        }
    }, [defaultProfile, assessmentProfile]);

    useEffect(() => {
      const interval = setInterval(() => {
        handleRefreshProfileList(); // Refresh from API
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval);
    }, []);

    return (
      <div className="p-4 md:p-6 lg:p-8">
        {!forAssessmentProfile ? (
          <div className="mb-6 md:mb-8">
            <div className="mb-6 md:mb-8">
              <div className="mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-indigo-800 mb-2">
                  Assessment Profile
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">
                  Choose the assessment profile that best matches the job
                  requirements to generate questionnaire
                </p>
                {!loadingProfile ? (
                  <div className="items-center w-full flex flex-col lg:flex-row gap-3 md:gap-4 justify-center">
                    {/* Custom Select component */}
                    <div className="w-full lg:w-1/2 flex flex-row justify-center items-center gap-2">
                      {profiles && Object.keys(profiles).length > 0 && (
                        <div className="w-full">
                          <CustomProfileSelect
                            options={profiles.map((profile) => profile)}
                            onChange={handleProfileSelection}
                            value={assessmentProfile ?? defaultProfile?._id}
                          />
                        </div>
                      )}

                      {!loadingProfile && Object.keys(profiles).length === 0 ? (
                        <div className="border-t border-gray-200 w-full md:w-4/5">
                          <div className="px-4 py-16 text-center">
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center justify-center opacity-10">
                                <svg
                                  className="h-64 w-64 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="1"
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  ></path>
                                </svg>
                              </div>
                              <div className="relative">
                                <svg
                                  className="mx-auto h-16 w-16 text-primary-500"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="1.5"
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                                  ></path>
                                </svg>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                  No assessment profiles yet
                                </h3>
                                <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                                  Assessment profiles help you create consistent
                                  evaluations across similar positions. Create
                                  your first profile to get started.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        ""
                      )}

                      <button
                        className="p-2 hover:bg-gray-100 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                        onClick={handleRefreshProfileList}
                        aria-label="Refresh profiles"
                      >
                        <RotateCw
                          className={`w-4 h-4 ${refreshProfile ? "animate-spin" : ""}`}
                        />
                      </button>
                    </div>

                    <Link
                      className="flex items-center justify-center gap-2 font-medium text-indigo-600 hover:text-indigo-700 py-2 px-3 rounded-md hover:bg-indigo-50 transition-colors min-h-[44px] w-full lg:w-auto"
                      to="/assessment/profile/new"
                      target="_blank"
                    >
                      <Plus size={20} />
                      <span className="text-sm md:text-base">Add profile</span>
                    </Link>
                  </div>
                ) : (
                  <label>Loading profile...</label>
                )}
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
        {!tempJobId ? (
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">
            Jobseeker Question Builder
          </h2>
        ) : forAssessmentProfile ? (
          <p className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-indigo-800 pb-2">
            Add Profile-Specific Questions for Candidate Assessment
          </p>
        ) : (
          <p className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-indigo-800 pb-2">
            Add Job-Specific Questions for Candidate Assessment
          </p>
        )}
        {!isAssessmentProfileApproved ? (
          <div className="border-2 border-solid border-green-400 flex flex-col sm:flex-row justify-between items-start sm:items-center w-full sm:w-3/4 lg:w-1/2 mx-auto p-3 sm:p-4 md:p-6 bg-white rounded-lg shadow-md mb-8 md:mb-10 gap-3">
            <div className="w-full sm:w-3/4">
              <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3">
                Approve Assessment Profile
              </h3>
              <em className="text-xs sm:text-sm md:text-base text-gray-600">
                After approval the assessment profile will be finalized for this
                job.
              </em>
            </div>
            <button
              className="bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded transition duration-300 ease-in-out w-full sm:w-auto whitespace-nowrap min-h-[44px] text-sm md:text-base"
              onClick={handleAssessmentProfileApproval}
            >
              Approve Profile
            </button>
          </div>
        ) : !isQuestionnaireApproved ? (
          <div className="border-2 border-solid border-indigo-400 flex flex-col sm:flex-row justify-between items-start sm:items-center w-full sm:w-3/4 lg:w-1/2 mx-auto p-3 sm:p-4 md:p-6 bg-white rounded-lg shadow-md mb-8 md:mb-10 gap-3">
            <div className="w-full sm:w-3/4">
              <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3">
                Approve Questionnaire
              </h3>
              <em className="text-xs sm:text-sm md:text-base text-gray-600">
                After approval question set can be sent out to jobseekers for
                assessment.
              </em>
            </div>
            <button
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-4 rounded transition duration-300 ease-in-out w-full sm:w-auto whitespace-nowrap min-h-[44px] text-sm md:text-base"
              onClick={handleQuestionnaireApproval}
            >
              Approve
            </button>
          </div>
        ) : null}
        {/* Background Loading Banner */}
        <BackgroundLoadingBanner
          isLoading={isBackgroundLoading}
          progress={loadingProgress}
        />
        <QuestionGenerator
          key={`QuestionGenerator1`}
          onQuestionsGenerated={handleQuestionsGenerated}
        />
        {/* Questions List */}
        <DraggableQuestionsList
          questions={filteredQuestions}
          onQuestionsReorder={async (newQuestions) => {
            // Update both filtered and all questions while maintaining order
            const questionsJSON = {
              jobId: mainJob,
              questions: JSON.parse(JSON.stringify(newQuestions)),
              deletedQuestions: deletedQuestions, // Include deleted questions array
              reorderd: true,
            };

            const questions = await saveQuestion(questionsJSON);
            if (questions.questions) {
              setFilteredQuestions(questions.questions);
            }
            //clear the deleted questions array after successful save
            setDeletedQuestions([]);
          }}
          renderItem={(question, index) => (
            <QuestionCard
              showProfileAddOption={!forAssessmentProfile}
              question={question}
              index={index}
              expanded={expandedQuestions.includes(question._id)}
              expandedQuestions={expandedQuestions}
              onToggleExpand={handleToggleExpand}
              onUpdate={(field, value) => {
                const updatedQuestions = filteredQuestions.map((q) => {
                  if (q._id === question._id) {
                    let updatedQuestion;

                    // Handle profile picture question detection
                    if (field === "question_text") {
                      let category = "General";
                      if (isUploadProfilePictureQuestion(value)) {
                        category = "Avatar"; // if the question is related to the picture upload than category should be Avatar only
                      }
                      updatedQuestion = {
                        ...q,
                        [field]: value,
                        category, // Set category to Avatar
                      };
                    }
                    // Handle answer_type changes
                    else if (field === "answer_type") {
                      updatedQuestion = {
                        ...q,
                        [field]: value,
                        question_type:
                          value === AnswerTypes.FILE ||
                            value === AnswerTypes.IMAGE
                            ? QuestionTypes.QUALIFICATION
                            : QuestionTypes.ASSESSMENT,
                      };
                    }
                    // Handle other field updates
                    else {
                      updatedQuestion = { ...q, [field]: value };
                    }

                    return updatedQuestion;
                  }
                  return q;
                });

                setFilteredQuestions(updatedQuestions);
              }}
              onDelete={handleDeleteQuestion}
              onAddFollowUp={handleAddFollowUp}
              onUpdateFollowUp={handleUpdateFollowUp}
              parentExpanded={true}
              handleAddQuestionToProfile={handleAddQuestionToProfile}
              handleRemoveQuestionFromProfile={handleRemoveQuestionFromProfile}
              assessmentProfile={assessmentProfile}
            />
          )}
          enableDragDrop={true}
        />
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 p-3 md:p-4 bg-white w-full">
          <button
            onClick={addNewQuestion}
            className="px-4 md:px-6 py-2 text-white rounded hover:bg-opacity-90 flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px] text-sm md:text-base transition-colors"
            style={{ backgroundColor: "#4c4def" }}
          >
            <PlusCircle size={20} className="flex-shrink-0" />
            <span>Add New Question</span>
          </button>

          {!forAssessmentProfile ? (
            <div className="flex flex-col w-full sm:w-auto">
              <button
                onClick={generateQuestions}
                className="px-4 md:px-6 py-2.5 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2 min-h-[44px] text-sm md:text-base transition-colors w-full"
                disabled={displayLoader}
              >
                <Brain size={20} className="flex-shrink-0" />
                <span>
                  {displayLoader ? "Generating..." : "Generate with AI"}
                </span>
                {displayLoader && (
                  <Oval
                    visible={true}
                    height="16"
                    width="16"
                    color="white"
                    ariaLabel="oval-loading"
                    wrapperClass="flex-shrink-0"
                  />
                )}
                <em className="text-xs text-center mt-1 text-white">
                (Takes 1-2 min)
                </em>
              </button>
            </div>
          ) : (
            ""
          )}
          {!tempJobId || forAssessmentProfile ? (
            <button
              onClick={handleSave}
              className="px-4 md:px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto text-sm md:text-base transition-colors"
            >
              <Save size={20} className="flex-shrink-0" />
              <span>Save Questions</span>
            </button>
          ) : (
            ""
          )}
        </div>
      </div>
    );
  },
);

export default QuestionnaireBuilder;
